/**
 * Suppliers (contract §4) — the master list plus inline creation during RFQ triage,
 * and the item's purchase/quote history that the supplier picker shows as context.
 *
 * `useSuppliers` now reads through `masters.get_suppliers` — the new
 * `quoflow.quoflow.quoflow.api.*` method namespace this migration moves onto (see `bosun.ts`'s
 * header) — instead of the generic `Supplier` resource. `useCreateSupplier` stays on the old
 * `client.ts` REST path: no `quoflow.*` method exists yet for inline supplier creation.
 */

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useItemQuoteHistory } from './useItemQuoteHistory'
import { callGet } from './bosun'
import { getCompany } from './company'
import { money, str, trimmed, type WireList } from './wire'
import { type BosunApiError } from './client'
import { withBosunError } from './useEnquiryMutations'
import { createSupplier } from './createSupplier'
import { qk } from './keys'
import type { ListResponse, PurchaseHistoryEntry, Supplier } from '../types'

/* ============ masters.get_suppliers ============ */

export interface WireSupplier {
  name: string
  supplier_name: string
  supplier_group?: string | null
  default_currency?: string | null
  primary_contact?: string | null
  platform_id?: string | null
  primary_email?: string | null
}

export function toSupplier(w: WireSupplier): Supplier {
  return {
    code: w.name,
    // Live returns a leading space on `supplier_name` — trimmed centrally by `wire.ts`'s
    // `trimmed()` (task 12) so the display name never carries whitespace the docname (identity)
    // doesn't (task-8b-brief.md).
    name: trimmed(w.supplier_name),
    /*
      READ NOW — the note that used to sit here (defect B3: "`primary_email` is `''` live for
      every supplier, so reading it would only hide the bug") is out of date.

      Re-measured 2026-08-31: 1 of the first 200 rows carries a real address. Sparse, but no
      longer nothing, and it is FREE — already on a list the picker fetches anyway — whereas
      `items.get_supplier_emails` costs one request PER SUPPLIER (~0.66s, no bulk form; `D9`).

      `''` therefore means "not on this list", NOT "this supplier has no address". Callers use it
      when present and fall back to the per-supplier lookup for the rest, so coverage improves on
      its own as `data_ops.import_supplier_email` backfills.
    */
    email: trimmed(w.primary_email),
    currency: w.default_currency ?? undefined,
  }
}

/**
 * `company` is sent on every call (Akhil, 2026-09-14 — "mandatory field now"): the enquiry's own
 * where the caller has one, else the session's operating company (`getCompany`, never empty).
 * Measured: the list answers with or without it today, so nothing here depends on the server
 * enforcing it — but the day it does, every caller is already compliant.
 */
export function useSuppliers(q?: string, company?: string) {
  const scope = company?.trim() || getCompany()
  return useQuery({
    queryKey: qk.suppliers(q, scope),
    queryFn: async () => {
      const page = await callGet<WireList<WireSupplier>>('masters', 'get_suppliers', {
        search: q,
        company: scope,
        limit: 200,
      })
      const result: ListResponse<Supplier> = {
        data: page.data.map(toSupplier),
        total: page.total ?? page.data.length,
        limit: page.limit ?? 200,
        offset: page.offset ?? 0,
      }
      return result
    },
    /*
      Keeps the previous list on screen while a new search runs.
      
      Without it every keystroke emptied the dialog and showed "Loading suppliers…" again — the
      search is ~1.1s against 2,831 suppliers, so the list spent more time blank than populated
      and the whole dialog read as broken. Now it updates in place once the new page lands, which
      is the same treatment `useItems` gives the catalogue picker for the same reason.
    */
    placeholderData: (previous) => previous,
  })
}

/**
 * A supplier created here is immediately pickable — the list is refetched on success.
 *
 * A6 (docs/bosun-required-api-round-2.md) — the specific ask that unblocks this hook. Not
 * proxied live (`vite.config.ts` routes `/api` wholesale now); `SupplierPickerModal.tsx`'s
 * `NewSupplierBar` disables its fields via `lib/api/liveAvailability.ts` rather than calling
 * this and 404ing. Kept as-is, unguarded, so it's ready to call the moment the endpoint exists.
 */
export function useCreateSupplier(options?: { onSuccess?: (supplier: Supplier) => void }) {
  const queryClient = useQueryClient()
  return useMutation<Supplier, BosunApiError, { name: string; email?: string }>({
    // LIVE since 2026-09-03 (`sales_person.create_supplier`, probed — see `createSupplier.ts`).
    // Was `api.post('/suppliers')`, a mock-only route, with the control disabled behind
    // `liveAvailability`'s `supplierCreate` flag; both are gone.
    mutationFn: withBosunError((body) => createSupplier(body.name, body.email ?? '')),
    onSuccess: (supplier) => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      options?.onSuccess?.(supplier)
    },
  })
}

/* ============ items.get_purchase_history ============ */

/**
 * Row shape is UNVERIFIED: the live `items.get_purchase_history` currently answers `[]`
 * regardless of arguments, so there is no real sample to confirm field names against. This
 * follows the flat-money convention `items.get_blanket_orders` uses (bare `rate` + `currency`,
 * not a nested object) since that's the one confirmed shape on this method namespace — read
 * defensively with `num`/`str` so an unexpected field never throws, only reads as blank.
 */
export interface WirePurchaseHistoryEntry {
  item_code?: string
  supplier?: string
  rate?: number
  currency?: string
  uom?: string
  ref?: string
  type?: string
  date?: string
  customer?: string
  remark?: string
}

export function toPurchaseHistoryEntry(w: WirePurchaseHistoryEntry): PurchaseHistoryEntry {
  return {
    item: str(w.item_code),
    supplier: str(w.supplier),
    price: money(w.rate, str(w.currency, 'AED')),
    uom: str(w.uom),
    ref: str(w.ref),
    type: w.type === 'PO' ? 'PO' : 'SQ',
    date: str(w.date),
    customer: str(w.customer),
    remark: str(w.remark),
  }
}

/** Last ~2 months of PO/SQ prices for an item — the live endpoint returns a bare array. */
/**
 * Past prices for an item — now served by the quotations themselves.
 *
 * ---- `items.get_purchase_history` is GONE, not merely empty ----
 *
 * It used to answer `[]` for every item, which was useless but harmless. Probed 2026-09-03 it no
 * longer exists at all:
 *
 *   GET items.get_purchase_history?item_code=0001030001
 *     → 417 ValidationError: module 'quoflow.quoflow.quoflow.api.items' has no attribute
 *       'get_purchase_history'
 *
 * This hook threw that straight at its two callers — `SupplierPickerModal` and the revisions
 * `SupplierChoiceModal` — turning a screen that used to show "no history" into one showing an
 * error. It is also absent from the new API collection, so the removal looks deliberate.
 *
 * `useItemQuoteHistory` already reads the real data for exactly this reason (its own header
 * records the same method failing), straight off `Supplier Quotation Item` + its parents. So this
 * delegates there rather than being deleted: both callers keep their shape and now get REAL
 * prices where they previously got an empty list from a broken endpoint.
 *
 * `months` is accepted and applied client-side. The underlying read takes the most recent rows and
 * has no window parameter of its own, so a caller asking for 24 months gets a window, not a
 * promise that nothing older exists beyond the row cap.
 */
export function usePurchaseHistory(item: string | null | undefined, months = 2) {
  const query = useItemQuoteHistory(item)
  const cutoff = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - months)
    // Site-local `YYYY-MM-DD`, to compare against wire dates as strings — never through a Date.
    // See `requirementFilters.ts` for why parsing the other side would shift a boundary.
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [months])

  const data = useMemo(
    // A row with no date is KEPT: "we do not know when" is not "it is too old", and dropping it
    // would silently hide a real past price.
    () => (query.data ?? []).filter((e) => e.date === '' || e.date >= cutoff),
    [query.data, cutoff],
  )

  return { ...query, data }
}
